/**
 * BrandFilmNode (Canvas V2)
 *
 * The last node of a create_brand_film DAG: it paces every generated insert to
 * its beat, builds the composition from the fixed editorial grid, renders it,
 * and lays the beat-locked narration over the music bed.
 *
 * Deliberately small, like CaptionerNode. Every input arrives by edge from the
 * nodes the Director built, so there is nothing to edit here. What you check
 * after a run is the finished film and how many beats went into it.
 *
 * Inputs:  in_clips (video, one per beat), in_voiceover (audio, one per beat),
 *          in_music (audio)
 * Output:  out_video (the finished film, with soundtrack)
 */

import { memo, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { NodeProps, Position } from 'reactflow'
import { getAssetUrl, type BrandFilmNodeData } from '@ponpon/api-client'
import { BaseNode, VideoPreviewPlayer, useNodeActions } from '../shared'
import { TypedHandle } from '../../TypedHandle'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

/** Beat count and brand from the plan; tolerant of a half-written plan. */
function summarize(plan: string | undefined): { beats: number; brand: string } {
  try {
    const parsed: { beats?: object[]; brand?: string } = JSON.parse(plan ?? '')
    return {
      beats: Array.isArray(parsed.beats) ? parsed.beats.length : 0,
      brand: typeof parsed.brand === 'string' ? parsed.brand : '',
    }
  } catch {
    return { beats: 0, brand: '' }
  }
}

const BrandFilmNode = memo(function BrandFilmNode(props: NodeProps<BrandFilmNodeData>) {
  const { data, selected, id } = props
  const [hovering, setHovering] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const projectId = useProjectId()
  const { handleDownload } = useNodeActions({ nodeId: id })
  const { beats, brand } = useMemo(() => summarize(data.in_plan), [data.in_plan])

  const videoUrl = data.out_video && projectId ? getAssetUrl(projectId, data.out_video) : null

  const onDownload = useCallback(() => {
    if (!data.out_video) return
    handleDownload(data.out_video, `${data.in_name || brand || 'brand-film'}.mp4`)
  }, [data.out_video, data.in_name, brand, handleDownload])

  return (
    <div onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      {/* One handle per wired input: ReactFlow drops an edge whose target
          handle is not rendered (error #008), and the node would look
          disconnected from its narration and music. */}
      <TypedHandle
        type="target"
        position={Position.Left}
        id="in_clips"
        portType="video"
        showTooltip
        style={{ top: '30%' }}
      />
      <TypedHandle
        type="target"
        position={Position.Left}
        id="in_voiceover"
        portType="audio"
        showTooltip
        style={{ top: '55%' }}
      />
      <TypedHandle
        type="target"
        position={Position.Left}
        id="in_music"
        portType="audio"
        showTooltip
        style={{ top: '80%' }}
      />

      <BaseNode
        title={data.in_name || (brand ? `${brand} — brand film` : 'Brand film')}
        icon="🎞️"
        selected={selected}
        status={data.status}
      >
        {videoUrl ? (
          <VideoPreviewPlayer
            videoUrl={videoUrl}
            isHovering={hovering}
            showExpand
            showDownload
            onExpand={() => setIsVideoModalOpen(true)}
            onDownload={onDownload}
          />
        ) : (
          <div className="flex h-24 items-center justify-center text-xs text-muted-foreground">
            Assembles once every beat has rendered
          </div>
        )}
        <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{beats > 0 ? `${beats} beats` : 'no plan yet'}</span>
          <span>{data.in_music ? 'scored' : 'no music'}</span>
        </div>
      </BaseNode>

      <TypedHandle type="source" position={Position.Right} id="out_video" portType="video" showTooltip />

      {isVideoModalOpen &&
        videoUrl &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-8 backdrop-blur-sm"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative flex max-h-full w-full max-w-6xl items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={videoUrl}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-lg bg-black shadow-2xl"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
})

export { BrandFilmNode }
